
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { BellIcon, Shield, HelpCircle, Users, Search, ClipboardList, User, LayoutDashboard, HandCoins, Bell, Building2, Calculator, List, Wrench, LineChart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/common/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { useBuyerAuth } from "@/hooks/useBuyerAuth";
import { useSellerNotifications } from "@/hooks/useSellerNotifications";
import { useBuyerNotifications } from "@/hooks/useBuyerNotifications";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";
import { generateInitials } from "@/utils/userUtils";
import NotificationDropdown from "./NotificationDropdown";
import BuyerNotificationDropdown from "./BuyerNotificationDropdown";
import { cn } from "@/lib/utils";

interface HeaderProps {
  title: string;
  context?: 'buyer' | 'seller';
}

// Wrapper components to conditionally call notification hooks
// This prevents unnecessary WebSocket subscriptions and Safari SecurityError crashes
interface NotificationData {
  unreadCount: number;
  markAllAsRead: () => Promise<void>;
}

const SellerNotificationProvider = ({ children }: { 
  children: (data: NotificationData) => React.ReactNode 
}) => {
  const { unreadCount, markAllAsRead } = useSellerNotifications();
  return <>{children({ unreadCount, markAllAsRead })}</>;
};

const BuyerNotificationProvider = ({ children }: { 
  children: (data: NotificationData) => React.ReactNode 
}) => {
  const { unreadCount, markAllAsRead } = useBuyerNotifications();
  return <>{children({ unreadCount, markAllAsRead })}</>;
};

const Header = ({ title, context = 'seller' }: HeaderProps) => {
  const { profile, signOut, user } = useAuth();
  const { buyerProfile } = useBuyer();
  const { signOutAsBuyer } = useBuyerAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const { topPadding } = useSafeAreaInsets();
  
  // Determine current profile based on context - enforce strict separation
  const currentProfile = context === 'buyer' ? buyerProfile : profile;
  
  // Generate user initials from profile name
  const userInitials = generateInitials(currentProfile?.full_name);
  
  // Get profile image URL - strictly separated by context
  const profileImageUrl = context === 'buyer' 
    ? buyerProfile?.profile_image_url 
    : profile?.profile_image_url;
  
  const handleLogout = () => {
    if (context === 'buyer') {
      signOutAsBuyer();
    } else {
      signOut();
    }
  };
  
  // Check admin status for seller context
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (context !== 'seller' || !user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const { data } = await (supabase as any)
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        setIsAdmin(data !== null);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user, context]);
  
  // Define navigation links based on context
  const profileLink = context === 'buyer' ? '/buyer/profile' : '/seller/profile';
  const dashboardLink = context === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard';

  // Select the appropriate notification provider based on context
  const NotificationProvider = context === 'seller' 
    ? SellerNotificationProvider 
    : BuyerNotificationProvider;
  
  return (
    <header className={cn("border-b border-border sticky top-0 z-10 bg-godyspo-background", topPadding)}>
      <div className="flex justify-between items-center h-12 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <Logo size="small" context={context} />
          <div className="hidden md:block font-medium">{title}</div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Notification Bell - conditionally uses the appropriate provider */}
          <NotificationProvider>
            {({ unreadCount, markAllAsRead }) => (
              <DropdownMenu onOpenChange={async (open) => {
                if (open && unreadCount > 0) {
                  await markAllAsRead();
                }
              }}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <BellIcon className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="absolute -top-1 -right-1 h-5 w-5 text-xs flex items-center justify-center p-0 min-w-[20px]"
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  {context === 'seller' ? <NotificationDropdown /> : <BuyerNotificationDropdown />}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </NotificationProvider>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                <Avatar className="h-9 w-9">
                  {profileImageUrl && (
                    <AvatarImage 
                      src={profileImageUrl} 
                      alt={currentProfile?.full_name || "User profile"} 
                    />
                  )}
                  <AvatarFallback className="bg-godyspo-blue text-white">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuItem asChild>
                <Link to={profileLink} className="flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={dashboardLink} className="flex items-center">
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
              {isAdmin && context === 'seller' && (
                <DropdownMenuItem asChild>
                  <Link to="/admin/dashboard" className="text-godyspo-gold font-medium">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Console
                  </Link>
                </DropdownMenuItem>
              )}
              {context === 'seller' && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/offers" className="flex items-center">
                      <HandCoins className="h-4 w-4 mr-2" />
                      Property Offers
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/properties/active" className="flex items-center">
                      <Building2 className="h-4 w-4 mr-2" />
                      My Properties
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/search-properties">
                      <Search className="h-4 w-4 mr-2" />
                      Search Properties
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/deal-tracker">
                      <ClipboardList className="h-4 w-4 mr-2" />
                      Deal Tracker
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/buyer-lists" className="flex items-center">
                      <List className="h-4 w-4 mr-2" />
                      My Buyer Lists
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/seller/tools" className="flex items-center">
                      <Wrench className="h-4 w-4 mr-2" />
                      Tools
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/market" className="flex items-center">
                      <LineChart className="h-4 w-4 mr-2" />
                      Housing Market
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              
            {context === 'buyer' && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/buyer/search" className="flex items-center">
                    <Search className="h-4 w-4 mr-2" />
                    Search Properties
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/buyer/offers" className="flex items-center">
                    <HandCoins className="h-4 w-4 mr-2" />
                    My Offers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/buyer/sellers" className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    Favorite Sellers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/buyer/repair-estimator" className="flex items-center">
                    <Calculator className="h-4 w-4 mr-2" />
                    Repair Estimator
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/buyer/profit-calculator" className="flex items-center">
                    <Calculator className="h-4 w-4 mr-2" />
                    Profit Calculator
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/market" className="flex items-center">
                    <LineChart className="h-4 w-4 mr-2" />
                    Housing Market
                  </Link>
                </DropdownMenuItem>
              </>
            )}
                    <DropdownMenuItem asChild>
                      <Link to={context === 'seller' ? "/seller/notifications" : "/buyer/notifications"} className="flex items-center">
                        <Bell className="h-4 w-4 mr-2" />
                        Notifications
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={context === 'seller' ? "/seller/help" : "/buyer/help"}>
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Help Center
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
